import { Drawer, useMediaQuery } from "@mui/material";
import Loader from "@/components/Loader/Loader";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { BellNavIcon } from "@/components/SVGComponents/MemberIcons";
import { useLanguage } from "@/contexts/LanguageContext";
import { combineClasses } from "@/lib/utils";
import type { NotificationLog } from "@/services/api/userProfile";
import styles from "./NotificationPanel.module.scss";

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  notifications: NotificationLog[];
  isLoading: boolean;
  unreadCount: number;
}

const getPrimaryText = (...values: unknown[]) =>
  values.find((value) => typeof value === "string" && value.trim()) as
    | string
    | undefined;

const formatNotificationTime = (dateValue?: string | null) => {
  if (!dateValue) {
    return "";
  }

  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const diffInMinutes = Math.round(
    (Date.now() - parsedDate.getTime()) / (1000 * 60),
  );

  if (diffInMinutes < 1) {
    return "Just now";
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes} min ago`;
  }

  const diffInHours = Math.round(diffInMinutes / 60);

  if (diffInHours < 24) {
    return `${diffInHours} hr ago`;
  }

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const NotificationPanel = ({
  open,
  onClose,
  notifications,
  isLoading,
  unreadCount,
}: NotificationPanelProps) => {
  const { t } = useLanguage();
  const isMobile = useMediaQuery("(max-width: 760px)");

  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: combineClasses(
            styles.paper,
            isMobile && styles.paperMobile,
          ),
        },
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.headingWrap}>
            <span className={styles.headingIcon}>
              <BellNavIcon />
            </span>

            <div className={styles.headingCopy}>
              <h2>{t(["notifications.title", "Notifications"], "Notifications")}</h2>
              <p>
                {unreadCount > 0
                  ? `${unreadCount} ${t(
                      ["notifications.unread", "unread updates"],
                      "unread updates",
                    )}`
                  : t(
                      ["notifications.allCaughtUp", "You are all caught up."],
                      "You are all caught up.",
                    )}
              </p>
            </div>
          </div>

          <button
            aria-label={t(["common.close", "Close"], "Close")}
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <CrossIcon />
          </button>
        </div>

        <div className={styles.body}>
          {isLoading ? (
            <div className={styles.stateBlock}>
              <Loader fullscreen={false} size={84} />
            </div>
          ) : notifications.length > 0 ? (
            <div className={styles.notificationList}>
              {notifications.map((notification, index) => {
                const title =
                  getPrimaryText(
                    notification.title,
                    notification.subject,
                    notification.name,
                    notification.type,
                  ) ||
                  t(
                    ["notifications.defaultTitle", "Notification"],
                    "Notification",
                  );
                const message =
                  getPrimaryText(
                    notification.message,
                    notification.description,
                    notification.body,
                    notification.content,
                    notification.text,
                  ) ||
                  t(
                    ["notifications.defaultMessage", "A new update is ready for you."],
                    "A new update is ready for you.",
                  );
                const isRead = Boolean(notification.isRead ?? notification.read);
                const timestamp = formatNotificationTime(
                  getPrimaryText(notification.createdAt, notification.updatedAt),
                );

                return (
                  <article
                    className={combineClasses(
                      styles.notificationCard,
                      !isRead && styles.notificationCardUnread,
                    )}
                    key={String(notification.id ?? `${title}-${index}`)}
                  >
                    <div className={styles.notificationTopRow}>
                      <h3>{title}</h3>
                      {!isRead ? (
                        <span className={styles.statusPill}>
                          {t(["notifications.new", "New"], "New")}
                        </span>
                      ) : null}
                    </div>

                    <p>{message}</p>

                    {timestamp ? (
                      <span className={styles.notificationTime}>{timestamp}</span>
                    ) : null}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={styles.stateBlock}>
              <span className={styles.emptyIcon}>
                <BellNavIcon />
              </span>
              <h3>
                {t(
                  ["notifications.emptyTitle", "No notifications yet"],
                  "No notifications yet",
                )}
              </h3>
              <p>
                {t(
                  [
                    "notifications.emptyBody",
                    "Important updates, reminders, and table activity will appear here.",
                  ],
                  "Important updates, reminders, and table activity will appear here.",
                )}
              </p>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default NotificationPanel;
